import { grey, cyan } from "@faro-lotv/flat-ui";
import { Box, Stack, Tab, Tabs } from "@mui/material";
import type { JSX, PropsWithChildren, ReactNode } from "react";
import type { AnalysisPanelTab } from "@/store/point-cloud-analysis-tool-slice";

/** @returns The shared, parent-constrained analysis surface for settings and results. */
export function AnalysisDockingPanel({
  children,
  embedded = false,
  fillHeight = false,
}: PropsWithChildren<{ embedded?: boolean; fillHeight?: boolean }>): ReactNode {
  if (embedded) return children;
  return (
    <Box
      component="section"
      aria-label="Point cloud analysis"
      sx={{
        p: 1.5,
        position: "relative",
        borderRadius: 1,
        bgcolor: grey[1000],
        color: grey[0],
        boxSizing: "border-box",
        minHeight: 0,
        height: fillHeight ? "100%" : undefined,
        display: "flex",
        flexDirection: "column",
        maxHeight: "100%",
        overflowY: "auto",
        width: 480,
        maxWidth: "min(100%, calc(100vw - 48px))",
      }}
    >
      <Stack
        sx={{
          gap: fillHeight ? 1 : 2,
          minWidth: 0,
          minHeight: 0,
          flex: fillHeight ? 1 : undefined,
          "& > *": { flexShrink: 0 },
        }}
      >
        {children}
      </Stack>
    </Box>
  );
}

/** @returns Tabs shared by draft settings and completed analyses. */
export function AnalysisPanelTabs({
  value,
  onChange,
}: {
  value: AnalysisPanelTab;
  onChange(value: AnalysisPanelTab): void;
}): JSX.Element {
  return (
    <Tabs
      value={value}
      onChange={(_, tab: unknown) => {
        if (tab === "cad" || tab === "polygon" || tab === "clashes") {
          onChange(tab);
        }
      }}
      aria-label="Analysis views"
      textColor="inherit"
      variant="fullWidth"
      sx={{
        minHeight: 44,
        "& .MuiTabs-indicator": { bgcolor: cyan[400] },
        "& .MuiTab-root": {
          color: grey[0],
          minWidth: 0,
          minHeight: 44,
          px: 1,
          fontSize: 12,
          lineHeight: 1.3,
          textTransform: "none",
        },
        "& .Mui-selected": { color: cyan[400] },
      }}
    >
      <Tab value="cad" label="CAD deviation" wrapped />
      <Tab value="polygon" label="Polygon analysis" wrapped />
      <Tab value="clashes" label="Clash Detection" wrapped />
    </Tabs>
  );
}
